import { useState } from "react";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "common/components/ui/Drawer";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useTargetingMutations from "../../../../datahooks/useTargetingMutations";
import SectionHeading from "../../../SectionHeading";
import StepperNextButton from "../../../StepperNextButton";
import Stepper from "../../Stepper";
import CsvOverview from "../CsvOverview";
import CsvUpload from "./CsvUpload";

import type { CsvState } from "../../../../types";

export default function List() {
  const isDesktop = useTwBreakpoint("lg");

  const [csvState, setCsvState] = useState<CsvState>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  const { parsed, mainIdentifier, columns } = csvState ?? {};

  const isFileError =
    !csvState ||
    !mainIdentifier ||
    !parsed.length ||
    !columns.every((col) => !!col);

  const { setTargeting, isSettingTargeting } = useTargetingMutations();

  function onNext() {
    setTargeting({
      campaignTargeting: csvState
        ? {
            profiles: parsed,
            url_column: mainIdentifier,
          }
        : { profiles: [], url_column: "empty" },
    });
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <>
      <SectionHeading
        title="Create targeting list"
        subtitle="Upload a .CSV file to create your campaign targeting"
      />

      {csvState ? (
        <CsvOverview
          csvState={csvState}
          setCsvState={setCsvState}
          isFileError={isFileError}
          onChange={() => setCsvState(null)}
        />
      ) : (
        <CsvUpload onSuccess={(data) => setCsvState(data)} />
      )}

      <Stepper
        selectedIndex={0}
        nextButtonComponent={
          <StepperNextButton
            isLoading={isSettingTargeting}
            disabled={csvState && isFileError}
            onClick={() => {
              if (!csvState) {
                setIsConfirmDialogOpen(true);
              } else {
                onNext();
              }
            }}
          />
        }
      />
      <Component
        open={isConfirmDialogOpen}
        onOpenChange={setIsConfirmDialogOpen}
      >
        <Content className="lg:w-[448px]">
          <Title>Skip this step?</Title>
          <Description className="mb-8">
            This will create a blank campaign. You can still add targets later
            through integrations, via API, or manually.
          </Description>
          <div className="flex items-center justify-between gap-x-2.5">
            <Button
              variant="secondary-black"
              onClick={() => setIsConfirmDialogOpen(false)}
              disabled={isSettingTargeting}
              className="lg:flex-1"
            >
              Cancel
            </Button>

            <Button
              isLoading={isSettingTargeting}
              className="lg:flex-1"
              onClick={onNext}
            >
              Skip targeting
            </Button>
          </div>
        </Content>
      </Component>
    </>
  );
}
